import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { 
  Brain,
  MessageSquare,
  Star,
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  Search,
  Filter,
  RefreshCw,
  AlertTriangle,
  Loader2,
  BookOpen,
  ThumbsUp,
  ThumbsDown,
  Eye,
  History,
  ChevronLeft,
  ChevronRight,
  Save,
  Paperclip,
  FileText,
  Image,
  File,
  Upload,
  Link2,
  Sparkles,
  Send
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import AyniChatAsistente from '../../components/AyniChatAsistente';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente de estrellas para calificación
function StarRating({ value, onChange, readOnly = false, size = 'md' }) {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readOnly}
          className={`${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110 transition-transform'}`}
          onMouseEnter={() => !readOnly && setHovered(star)}
          onMouseLeave={() => !readOnly && setHovered(0)}
          onClick={() => !readOnly && onChange?.(star)}
        >
          <Star 
            className={`${sizeClass} ${
              star <= (hovered || value) 
                ? 'fill-yellow-400 text-yellow-400' 
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// Componente para una conversación individual
function ConversacionCard({ conversacion, onCalificar, onCorregir, onVerDetalle }) {
  const [showCorreccion, setShowCorreccion] = useState(false);
  const [correccion, setCorreccion] = useState('');
  const [nota, setNota] = useState('');
  const [tipoFeedback, setTipoFeedback] = useState('correccion');
  
  const tieneCorreccion = !!conversacion.correccion;
  const tieneCalificacion = !!conversacion.calificacion;
  
  return (
    <Card className={`${tieneCorreccion ? 'border-green-200 bg-green-50/30' : conversacion.necesita_correccion ? 'border-red-200 bg-red-50/30' : ''}`}>
      <CardContent className="p-4 space-y-3">
        {/* Header con teléfono y fecha */}
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-xs">
              {conversacion.telefono?.slice(-4) ? `...${conversacion.telefono.slice(-4)}` : 'N/A'}
            </Badge>
            <span>{new Date(conversacion.timestamp).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</span>
          </div>
          <div className="flex items-center gap-2">
            {tieneCorreccion && (
              <Badge className="bg-green-100 text-green-700 border-green-200">
                <Check className="h-3 w-3 mr-1" />
                Corregida
              </Badge>
            )}
            {conversacion.necesita_correccion && !tieneCorreccion && (
              <Badge className="bg-red-100 text-red-700 border-red-200">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Revisar
              </Badge>
            )}
          </div>
        </div>
        
        {/* Mensaje del usuario */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
          <p className="text-xs text-blue-600 font-medium mb-1">Usuario preguntó:</p>
          <p className="text-gray-800">{conversacion.mensaje_usuario}</p>
        </div>
        
        {/* Respuesta de la IA */}
        <div className="bg-gray-50 border-l-4 border-gray-300 p-3 rounded">
          <p className="text-xs text-gray-500 font-medium mb-1">Ayni respondió:</p>
          <pre className="text-gray-800 whitespace-pre-wrap font-sans text-sm max-h-32 overflow-auto">
            {conversacion.respuesta_ia}
          </pre>
        </div>
        
        {/* Corrección/Feedback (si existe) */}
        {tieneCorreccion && (
          <div className={`border-l-4 p-3 rounded ${
            conversacion.correccion.tipo === 'takeover' ? 'bg-orange-50 border-orange-400' :
            conversacion.correccion.tipo === 'feedback' ? 'bg-blue-50 border-blue-400' :
            conversacion.correccion.tipo === 'incompleta' ? 'bg-yellow-50 border-yellow-400' :
            'bg-green-50 border-green-400'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">
                {conversacion.correccion.tipo === 'takeover' ? '👤' :
                 conversacion.correccion.tipo === 'feedback' ? '💬' :
                 conversacion.correccion.tipo === 'incompleta' ? '📝' : '✏️'}
              </span>
              <p className={`text-xs font-medium ${
                conversacion.correccion.tipo === 'takeover' ? 'text-orange-600' :
                conversacion.correccion.tipo === 'feedback' ? 'text-blue-600' :
                conversacion.correccion.tipo === 'incompleta' ? 'text-yellow-700' :
                'text-green-600'
              }`}>
                {conversacion.correccion.tipo === 'takeover' ? 'Debió hacer takeover' :
                 conversacion.correccion.tipo === 'feedback' ? 'Feedback/Devolución' :
                 conversacion.correccion.tipo === 'incompleta' ? 'Información incompleta' :
                 'Respuesta corregida'} 
                <span className="font-normal">por {conversacion.correccion.corregido_por}</span>
              </p>
            </div>
            <pre className="text-gray-800 whitespace-pre-wrap font-sans text-sm">
              {conversacion.correccion.respuesta_correcta}
            </pre>
            {conversacion.correccion.nota && (
              <p className="text-xs text-gray-500 mt-2 italic">
                Nota: {conversacion.correccion.nota}
              </p>
            )}
          </div>
        )}
        
        {/* Calificación y acciones */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Calificación:</span>
            <StarRating 
              value={conversacion.calificacion?.puntuacion || 0}
              onChange={(val) => onCalificar(conversacion.id, val)}
              size="sm"
            />
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowCorreccion(!showCorreccion)}
              className={tieneCorreccion ? 'text-green-600' : ''}
            >
              <Edit className="h-4 w-4 mr-1" />
              {tieneCorreccion ? 'Ver corrección' : 'Corregir'}
            </Button>
          </div>
        </div>
        
        {/* Formulario de corrección/feedback */}
        {showCorreccion && !tieneCorreccion && (
          <div className="border-t pt-3 space-y-3">
            {/* Tipo de feedback */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Tipo de feedback:</label>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant={tipoFeedback === 'correccion' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setTipoFeedback('correccion')}
                >
                  ✏️ Respuesta correcta
                </Button>
                <Button 
                  variant={tipoFeedback === 'feedback' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setTipoFeedback('feedback')}
                >
                  💬 Devolución/Feedback
                </Button>
                <Button 
                  variant={tipoFeedback === 'takeover' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setTipoFeedback('takeover')}
                >
                  👤 Debió hacer takeover
                </Button>
                <Button 
                  variant={tipoFeedback === 'incompleta' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setTipoFeedback('incompleta')}
                >
                  📝 Info incompleta
                </Button>
              </div>
            </div>
            
            {/* Contenido según tipo */}
            {tipoFeedback === 'correccion' && (
              <Textarea
                value={correccion}
                onChange={(e) => setCorreccion(e.target.value)}
                placeholder="Escribí la respuesta correcta que debería haber dado la IA..."
                rows={4}
              />
            )}
            
            {tipoFeedback === 'feedback' && (
              <Textarea
                value={correccion}
                onChange={(e) => setCorreccion(e.target.value)}
                placeholder="Explicale a la IA qué le faltó, qué debería mejorar, qué contexto no tuvo en cuenta..."
                rows={4}
              />
            )}
            
            {tipoFeedback === 'takeover' && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Indicar que en este caso la IA debería haber ofrecido pasar la conversación a un humano.
                </p>
                <Textarea
                  value={correccion}
                  onChange={(e) => setCorreccion(e.target.value)}
                  placeholder="¿Por qué debería haber hecho takeover? (ej: tema sensible, usuario frustrado, pregunta muy específica...)"
                  rows={3}
                />
              </div>
            )}
            
            {tipoFeedback === 'incompleta' && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  La respuesta fue correcta pero le faltó información importante.
                </p>
                <Textarea
                  value={correccion}
                  onChange={(e) => setCorreccion(e.target.value)}
                  placeholder="¿Qué información le faltó incluir? (ej: no mencionó el horario, faltó el link, no ofreció alternativas...)"
                  rows={3}
                />
              </div>
            )}
            
            <Input
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Nota adicional (opcional)"
            />
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => {
                setShowCorreccion(false);
                setTipoFeedback('correccion');
              }}>
                Cancelar
              </Button>
              <Button 
                size="sm" 
                onClick={() => {
                  onCorregir(conversacion.id, correccion, nota, tipoFeedback);
                  setShowCorreccion(false);
                  setCorreccion('');
                  setNota('');
                  setTipoFeedback('correccion');
                }}
                disabled={!correccion.trim()}
              >
                <Save className="h-4 w-4 mr-1" />
                Guardar Feedback
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminIATrainingPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chat');
  
  // Estado para conocimiento
  const [conocimiento, setConocimiento] = useState([]);
  const [editingConocimiento, setEditingConocimiento] = useState(null);
  
  // Estado para conversaciones
  const [conversaciones, setConversaciones] = useState([]);
  const [filtros, setFiltros] = useState({
    telefono: '',
    solo_corregidas: false,
    solo_sin_corregir: false,
    limite: 50
  });
  
  // Estado para estadísticas
  const [stats, setStats] = useState(null);
  const [statsConv, setStatsConv] = useState(null);
  
  // Estado para mensajes
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Función para subir archivo (múltiples)
  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    try {
      setUploading(true);
      const archivosActuales = editingConocimiento.archivos || [];
      const nuevosArchivos = [...archivosActuales];
      
      for (const file of files) {
        // Validar tamaño (10MB)
        if (file.size > 10 * 1024 * 1024) {
          setError(`El archivo "${file.name}" es demasiado grande. Máximo 10MB.`);
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API_URL}/ia/conocimiento/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.success) {
          const fullUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
          nuevosArchivos.push({
            url: fullUrl,
            nombre: response.data.original_name,
            tipo: response.data.tipo
          });
        }
      }
      
      setEditingConocimiento({
        ...editingConocimiento,
        archivos: nuevosArchivos
      });
      
      setSuccessMsg(`${files.length > 1 ? 'Archivos subidos' : 'Archivo subido'} correctamente`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  // Función para eliminar un archivo de la lista
  const handleRemoveArchivo = (index) => {
    const archivosActuales = editingConocimiento.archivos || [];
    const nuevosArchivos = archivosActuales.filter((_, i) => i !== index);
    setEditingConocimiento({
      ...editingConocimiento,
      archivos: nuevosArchivos
    });
  };

  // Cargar datos
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [conocimientoRes, statsRes, statsConvRes] = await Promise.all([
        axios.get(`${API_URL}/ia/conocimiento`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/ia/stats/calificaciones`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/ia/conversaciones/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setConocimiento(conocimientoRes.data);
      setStats(statsRes.data);
      setStatsConv(statsConvRes.data);
      setError(null);
    } catch (err) {
      setError('Error al cargar los datos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadConversaciones = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/ia/conversaciones`, filtros, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversaciones(response.data);
    } catch (err) {
      console.error('Error cargando conversaciones:', err);
    }
  }, [token, filtros]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'conversaciones') {
      loadConversaciones();
    }
  }, [activeTab, loadConversaciones]);

  // Funciones de conocimiento
  const saveConocimiento = async () => {
    if (!editingConocimiento) return;
    
    try {
      setSaving(true);
      if (editingConocimiento.id) {
        await axios.put(`${API_URL}/ia/conocimiento/${editingConocimiento.id}`, editingConocimiento, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/ia/conocimiento`, editingConocimiento, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setSuccessMsg('Conocimiento guardado');
      setEditingConocimiento(null);
      loadData();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Error al guardar el conocimiento');
    } finally {
      setSaving(false);
    }
  };

  const deleteConocimiento = async (id) => {
    if (!confirm('¿Eliminar este conocimiento?')) return;
    
    try {
      await axios.delete(`${API_URL}/ia/conocimiento/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMsg('Conocimiento eliminado');
      loadData();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Error al eliminar el conocimiento');
    }
  };

  // Funciones de conversaciones
  const calificarConversacion = async (id, puntuacion) => {
    try {
      await axios.post(`${API_URL}/ia/conversaciones/${id}/calificar`, { puntuacion }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadConversaciones();
      loadData(); // Actualizar stats
    } catch (err) {
      setError('Error al calificar');
    }
  };

  const corregirConversacion = async (id, respuestaCorrecta, nota, tipo = 'correccion') => {
    try {
      await axios.post(`${API_URL}/ia/conversaciones/${id}/corregir`, {
        conversacion_id: id,
        respuesta_original: '',
        respuesta_correcta: respuestaCorrecta,
        nota: nota,
        tipo: tipo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMsg('Corrección guardada');
      loadConversaciones();
      loadData();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError('Error al guardar corrección');
    }
  };

  const categorias = [
    { value: 'tramites', label: 'Trámites' },
    { value: 'valores', label: 'Valores y Pagos' },
    { value: 'horarios', label: 'Horarios y Contacto' },
    { value: 'bonificaciones', label: 'Bonificaciones' },
    { value: 'faqs', label: 'FAQs Generales' },
    { value: 'correcciones', label: 'Correcciones Frecuentes' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ia-training-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="h-6 w-6 text-purple-600" />
            Entrenamiento de IA
          </h1>
          <p className="text-gray-600 text-sm">
            Mejorá las respuestas de Ayni agregando conocimiento y corrigiendo errores
          </p>
        </div>
        <Button onClick={loadData} variant="outline" data-testid="btn-refresh">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Mensajes */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="h-4 w-4" />
          {successMsg}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_conversaciones || 0}</p>
                <p className="text-xs text-gray-500">Conversaciones</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.promedio_calificacion || 0}</p>
                <p className="text-xs text-gray-500">Promedio ⭐</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statsConv?.corregidas || 0}</p>
                <p className="text-xs text-gray-500">Corregidas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.necesitan_correccion || 0}</p>
                <p className="text-xs text-gray-500">Por revisar</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="chat" className="flex items-center gap-2" data-testid="tab-chat-ayni">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Chatear con Ayni</span>
            <span className="sm:hidden">Chat</span>
          </TabsTrigger>
          <TabsTrigger value="conversaciones" className="flex items-center gap-2" data-testid="tab-conversaciones">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Conversaciones</span>
            <span className="sm:hidden">Conv.</span>
          </TabsTrigger>
          <TabsTrigger value="conocimiento" className="flex items-center gap-2" data-testid="tab-conocimiento">
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Conocimiento</span>
            <span className="sm:hidden">Q&A</span>
          </TabsTrigger>
          <TabsTrigger value="estadisticas" className="flex items-center gap-2" data-testid="tab-estadisticas">
            <Star className="h-4 w-4" />
            <span className="hidden sm:inline">Estadísticas</span>
            <span className="sm:hidden">Stats</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Chat con Ayni Asistente */}
        <TabsContent value="chat" className="space-y-4">
          <AyniChatAsistente />
        </TabsContent>

        {/* Tab: Conversaciones */}
        <TabsContent value="conversaciones" className="space-y-4">
          {/* Filtros */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs">Buscar por teléfono</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      value={filtros.telefono}
                      onChange={(e) => setFiltros({...filtros, telefono: e.target.value})}
                      placeholder="Últimos 4 dígitos..."
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={filtros.solo_sin_corregir}
                    onCheckedChange={(v) => setFiltros({...filtros, solo_sin_corregir: v, solo_corregidas: v ? false : filtros.solo_corregidas})}
                  />
                  <Label className="text-sm">Solo sin corregir</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={filtros.solo_corregidas}
                    onCheckedChange={(v) => setFiltros({...filtros, solo_corregidas: v, solo_sin_corregir: v ? false : filtros.solo_sin_corregir})}
                  />
                  <Label className="text-sm">Solo corregidas</Label>
                </div>
                <Button onClick={loadConversaciones} data-testid="btn-filtrar">
                  <Filter className="h-4 w-4 mr-2" />
                  Filtrar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lista de conversaciones */}
          <div className="space-y-4">
            {conversaciones.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay conversaciones para mostrar</p>
                  <p className="text-sm">Las conversaciones de la IA aparecerán aquí</p>
                </CardContent>
              </Card>
            ) : (
              conversaciones.map((conv) => (
                <ConversacionCard
                  key={conv.id}
                  conversacion={conv}
                  onCalificar={calificarConversacion}
                  onCorregir={corregirConversacion}
                />
              ))
            )}
          </div>
        </TabsContent>

        {/* Tab: Conocimiento */}
        <TabsContent value="conocimiento" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Base de Conocimiento</CardTitle>
                  <CardDescription>
                    Agregá información que la IA usará para responder preguntas
                  </CardDescription>
                </div>
                <Button onClick={() => setEditingConocimiento({ categoria: 'faqs', titulo: '', contenido: '', activo: true, orden: conocimiento.length, archivos: [] })} data-testid="btn-add-conocimiento">
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {conocimiento.length === 0 ? (
                <div className="text-center p-8 text-gray-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay conocimiento agregado aún</p>
                  <p className="text-sm">Agregá preguntas frecuentes y respuestas para mejorar la IA</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conocimiento.map((item) => {
                    // Compatibilidad: convertir formato anterior a nuevo
                    const archivos = item.archivos || (item.archivo_url ? [{url: item.archivo_url, nombre: item.archivo_nombre, tipo: item.archivo_tipo}] : []);
                    const tieneArchivos = archivos.length > 0;
                    
                    return (
                    <div key={item.id} className={`border rounded-lg p-4 ${item.activo ? 'bg-white' : 'bg-gray-100 opacity-60'}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="outline">{item.categoria}</Badge>
                            <h4 className="font-medium">{item.titulo}</h4>
                            {!item.activo && <Badge variant="secondary">Inactivo</Badge>}
                            {tieneArchivos && (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                <Paperclip className="h-3 w-3 mr-1" />
                                {archivos.length} {archivos.length === 1 ? 'archivo' : 'archivos'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.contenido}</p>
                          {tieneArchivos && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {archivos.map((archivo, idx) => (
                                <a 
                                  key={idx}
                                  href={archivo.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="flex items-center gap-1 text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded"
                                >
                                  {archivo.tipo === 'pdf' && <FileText className="h-3 w-3" />}
                                  {archivo.tipo === 'imagen' && <Image className="h-3 w-3" />}
                                  {(!archivo.tipo || archivo.tipo === 'documento' || archivo.tipo === 'otro') && <File className="h-3 w-3" />}
                                  {archivo.nombre || `Archivo ${idx + 1}`}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setEditingConocimiento({...item, archivos: archivos})}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteConocimiento(item.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Estadísticas */}
        <TabsContent value="estadisticas" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Distribución de calificaciones */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución de Calificaciones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = stats?.distribucion_estrellas?.[String(star)] || 0;
                    const total = stats?.calificadas || 1;
                    const percentage = Math.round((count / total) * 100) || 0;
                    
                    return (
                      <div key={star} className="flex items-center gap-3">
                        <div className="flex items-center gap-1 w-20">
                          <span className="text-sm font-medium">{star}</span>
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        </div>
                        <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${star >= 4 ? 'bg-green-500' : star === 3 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 w-16 text-right">{count} ({percentage}%)</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Resumen */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Resumen General</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Total conversaciones</span>
                    <span className="font-bold text-lg">{stats?.total_conversaciones || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Calificadas</span>
                    <span className="font-bold text-lg text-green-600">{stats?.calificadas || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Sin calificar</span>
                    <span className="font-bold text-lg text-gray-500">{stats?.sin_calificar || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <span className="text-yellow-700">Promedio</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg text-yellow-700">{stats?.promedio_calificacion || 0}</span>
                      <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-200">
                    <span className="text-red-700">Necesitan corrección</span>
                    <span className="font-bold text-lg text-red-600">{stats?.necesitan_correccion || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info sobre cómo funciona */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-600" />
                ¿Cómo funciona el entrenamiento?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-blue-600">
                    <BookOpen className="h-5 w-5" />
                    <h4 className="font-medium">1. Conocimiento Base</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Agregá preguntas y respuestas que la IA incorporará a su base de conocimiento. 
                    Esto se suma al prompt del sistema.
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-yellow-600">
                    <Star className="h-5 w-5" />
                    <h4 className="font-medium">2. Calificaciones</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Calificá las respuestas de la IA. Las calificaciones bajas (1-2 ⭐) 
                    marcan la conversación para revisión y corrección.
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="h-5 w-5" />
                    <h4 className="font-medium">3. Correcciones</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Las correcciones que hagas se usan como ejemplos. La IA aprenderá 
                    de tus correcciones para dar mejores respuestas en casos similares.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Editar Conocimiento */}
      <Dialog open={!!editingConocimiento} onOpenChange={() => setEditingConocimiento(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingConocimiento?.id ? 'Editar Conocimiento' : 'Agregar Conocimiento'}
            </DialogTitle>
            <DialogDescription>
              Esta información se agregará al contexto de la IA
            </DialogDescription>
          </DialogHeader>

          {editingConocimiento && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Categoría</Label>
                  <Select 
                    value={editingConocimiento.categoria} 
                    onValueChange={(v) => setEditingConocimiento({...editingConocimiento, categoria: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categorias.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Switch
                    checked={editingConocimiento.activo}
                    onCheckedChange={(v) => setEditingConocimiento({...editingConocimiento, activo: v})}
                  />
                  <Label>Activo</Label>
                </div>
              </div>

              <div>
                <Label>Título / Pregunta</Label>
                <Input 
                  value={editingConocimiento.titulo}
                  onChange={(e) => setEditingConocimiento({...editingConocimiento, titulo: e.target.value})}
                  placeholder="Ej: ¿Cuál es el horario de atención?"
                />
              </div>

              <div>
                <Label>Contenido / Respuesta</Label>
                <Textarea 
                  value={editingConocimiento.contenido}
                  onChange={(e) => setEditingConocimiento({...editingConocimiento, contenido: e.target.value})}
                  placeholder="La respuesta que la IA debe dar..."
                  rows={6}
                />
              </div>

              {/* Sección de Archivos Adjuntos */}
              <div className="border-t pt-4 mt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <Paperclip className="h-4 w-4" />
                  Archivos Adjuntos (opcional)
                </Label>
                <p className="text-xs text-gray-500 mb-3">
                  Podés adjuntar PDFs, imágenes o documentos que la IA enviará cuando el usuario pregunte sobre este tema.
                </p>
                
                <div className="space-y-3">
                  {/* Lista de archivos subidos */}
                  {editingConocimiento.archivos && editingConocimiento.archivos.length > 0 && (
                    <div className="space-y-2">
                      {editingConocimiento.archivos.map((archivo, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                          {archivo.tipo === 'pdf' && <FileText className="h-4 w-4 text-red-500" />}
                          {archivo.tipo === 'imagen' && <Image className="h-4 w-4 text-blue-500" />}
                          {(!archivo.tipo || archivo.tipo === 'documento' || archivo.tipo === 'otro') && <File className="h-4 w-4 text-gray-500" />}
                          <span className="text-sm text-green-700 flex-1 truncate">
                            {archivo.nombre || 'Archivo'}
                          </span>
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm"
                            className="h-6 px-2"
                            onClick={() => window.open(archivo.url, '_blank')}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button 
                            type="button"
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 h-6 px-2"
                            onClick={() => handleRemoveArchivo(index)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Botón de subir archivo */}
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      id="archivo-upload"
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx"
                      multiple
                      onChange={handleFileUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('archivo-upload').click()}
                      disabled={uploading}
                      className={`w-full border-dashed border-2 hover:bg-gray-50 ${editingConocimiento.archivos?.length > 0 ? 'h-12' : 'h-20'}`}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="h-5 w-5 mr-2" />
                          {editingConocimiento.archivos?.length > 0 ? 'Agregar más archivos' : 'Subir archivos (PDF, imagen, documento)'}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-400 text-center">
                      Máximo 10MB por archivo. Podés seleccionar varios a la vez.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingConocimiento(null)}>Cancelar</Button>
            <Button onClick={saveConocimiento} disabled={saving || !editingConocimiento?.titulo || !editingConocimiento?.contenido}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
